export interface DynamicConfigurations {
  dsn: string;
  sentryEnvironment: string;
  whitelabel: string;
  /**
   * mdm-user-settings rollout phase (1/2/3), read at container startup from
   * the deployed environment's USER_SETTINGS_PHASE var. One build artifact is
   * deployed to every environment, so this must come from runtime config
   * (config.json), not a Vite build-time env var. Defaults to 1 (safe/legacy)
   * whenever unset or malformed.
   */
  userSettingsPhase: number;
}

export const defaultConfigurations: DynamicConfigurations = {
  dsn: '',
  sentryEnvironment: '',
  whitelabel: 'gatehouse',
  userSettingsPhase: 1,
};

export const configurationUrl = './configurations/config.json';

const getWhiteLabel = () => {
  const hostname = window.location.hostname;
  if (hostname === 'robotic.inc') {
    return 'robotic_inc';
  } else if (hostname === 'dominion.ghmaritime.com') {
    return 'sentinel-proactive';
  } else {
    return defaultConfigurations.whitelabel;
  }
};

export function mapDynamicConfigurations(
  config: DynamicConfigurations,
): DynamicConfigurations {
  if (typeof config !== 'object' || config === null) {
    return defaultConfigurations;
  }

  return {
    dsn: config?.dsn || defaultConfigurations.dsn,
    sentryEnvironment:
      config?.sentryEnvironment || defaultConfigurations.sentryEnvironment,
    whitelabel: getWhiteLabel(),
    userSettingsPhase: normalizeUserSettingsPhase(config?.userSettingsPhase),
  };
}

/**
 * Clamps to the three valid phases; anything malformed/out-of-range falls
 * back to phase 1 (legacy blob only) so a bad env var can't accidentally
 * enable dual-write or service-only behaviour.
 */
function normalizeUserSettingsPhase(value: unknown): number {
  const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
  if (n === 2 || n === 3) return n;
  return defaultConfigurations.userSettingsPhase;
}

/**
 * Fetch and map dynamic config before React mounts.
 * Returns defaults on any failure so the app still boots.
 */
export async function fetchConfig(): Promise<DynamicConfigurations> {
  try {
    const response = await fetch(configurationUrl);
    if (!response.ok) {
      throw new Error(
        `Error while fetching global config (${response.status})`,
      );
    }
    const raw: DynamicConfigurations = await response.json();
    return mapDynamicConfigurations(raw);
  } catch (error) {
    console.error(error);
    return defaultConfigurations;
  }
}
